import { useState, useEffect, useCallback } from 'react';
import VIPTierCard from './vip/VIPTierCard';
import VIPRegistrationModal from './vip/VIPRegistrationModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Founding 100 Tier section — orchestration only.
 * Pricing cards: `VIPTierCard`; registration flow: `VIPRegistrationModal`.
 */
export default function VIPSection() {
  const [counts, setCounts] = useState({
    part_time: { claimed: 0, total: 75 },
    full_time: { claimed: 0, total: 25 },
  });
  const [selectedTier, setSelectedTier] = useState(null);

  const refreshCounts = useCallback(() => {
    fetch(`${API}/api/vip/counts`)
      .then((r) => r.json())
      .then(setCounts)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  return (
    <section
      id="founding-100"
      className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-[#050505] text-white relative overflow-hidden"
      data-testid="vip-section"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 via-transparent to-[#00D26A]/5 pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/40 mb-6">
            <span className="text-[#FFD700] text-sm font-bold tracking-wider">
              FOUNDING 100 · LIMITED
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Skip the Beta.{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#00D26A] to-[#FFD700]">
              Own the Launch.
            </span>
          </h2>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
            For resellers who want the polished product without the beta-testing grind.
            <br />
            100 founding spots. 90% off the first 3 years. Never offered again.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-14 max-w-3xl mx-auto text-sm">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-xl p-5">
            <div className="font-bold text-[#00D26A] mb-2">🧪 Beta Tester (free)</div>
            <p className="text-zinc-400">
              Join early, help shape the product, report bugs. For tinkerers and contributors.
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-[#FFD700]/40 rounded-xl p-5">
            <div className="font-bold text-[#FFD700] mb-2">⭐ Founding 100 (VIP)</div>
            <p className="text-zinc-300">
              Skip the bug-hunting. Join at launch with the polished product. For busy resellers
              who want tools, not homework.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <VIPTierCard tierKey="part_time" count={counts.part_time} onReserve={setSelectedTier} />
          <VIPTierCard tierKey="full_time" count={counts.full_time} onReserve={setSelectedTier} />
        </div>

        <p className="text-center text-zinc-500 text-sm mt-10 max-w-2xl mx-auto">
          No payment taken today. You&rsquo;re joining a waiting list to claim this offer at
          launch in September 2026. Claim via private checkout link — first come, first served.
        </p>
      </div>

      {selectedTier && (
        <VIPRegistrationModal
          tierKey={selectedTier}
          onClose={() => setSelectedTier(null)}
          onRegistered={refreshCounts}
        />
      )}
    </section>
  );
}
